import _ from 'lodash';
import moment from 'moment-timezone';
import { styled } from 'styled-components';

import { useState } from 'react';

import * as api from '../../../../../../api';
import {
  TIME_OF_DAY_FORMAT,
  getFormattedDate,
} from '../../../../../../utils/date';
import { Button, Loader } from '@vivaahealth/design-system';
import { ShowMoreButton } from '../../../../../common';

interface Props {
  slots: api.EhrTimeSlot.EhrTimeSlot[];
  timezone: string;
  onAppointmentSelected: (slot: api.EhrTimeSlot.EhrTimeSlot) => void;
  isLoading: boolean;
  disabled: boolean;
}

const EhrTimeSlots: React.FC<Props> = ({
  slots,
  timezone,
  onAppointmentSelected,
  isLoading,
  disabled,
}) => {
  const [showAllAppointments, setShowAllAppointments] = useState(false);
  if (isLoading) {
    return <Loader />;
  }
  const dayGroup = _.groupBy(slots, (apt) =>
    timezone
      ? moment(apt.startTime).tz(timezone).startOf('day').format()
      : moment(apt.startTime).startOf('day').format(),
  );
  const dayTimes = Object.keys(dayGroup).sort();
  const firstAvailableDayTime = dayTimes[0];

  const renderTimeSlots = (args: {
    timeSlots?: api.EhrTimeSlot.EhrTimeSlot[];
    timezone: string;
    onAppointmentSelected: (appointment: api.EhrTimeSlot.EhrTimeSlot) => void;
  }) => {
    const { timeSlots, timezone, onAppointmentSelected } = args;
    if (!timeSlots?.length) return null;
    return (
      <TimeSlotWrapper>
        {timeSlots.map((slot) => {
          return (
            <TimeSlotButton
              disabled={disabled}
              data-test-id="time-slot-button"
              key={slot.id}
              onClick={() => onAppointmentSelected(slot)}
              children={
                timezone
                  ? moment(slot.startTime)
                      .tz(timezone)
                      .format(TIME_OF_DAY_FORMAT)
                  : moment(slot.startTime).format(TIME_OF_DAY_FORMAT)
              }
            />
          );
        })}
      </TimeSlotWrapper>
    );
  };

  return (
    <EhrTimeSlotContainer>
      <div>
        <DateTime $marginTopDisabled>
          {getFormattedDate(firstAvailableDayTime, timezone)}
        </DateTime>
        <div>
          {renderTimeSlots({
            timeSlots: dayGroup[firstAvailableDayTime],
            timezone,
            onAppointmentSelected,
          })}
        </div>
      </div>
      {dayTimes.length > 1 && !showAllAppointments && (
        <ShowMoreButton
          type="button"
          disabled={disabled}
          onClick={() => setShowAllAppointments(true)}
        >
          More appointments
        </ShowMoreButton>
      )}
      {showAllAppointments &&
        dayTimes.slice(1).map((dayTime, i) => {
          return (
            <div key={dayTime}>
              <DateTime>{getFormattedDate(dayTime, timezone)}</DateTime>
              <div>
                {renderTimeSlots({
                  timeSlots: dayGroup[dayTime],
                  timezone,
                  onAppointmentSelected,
                })}
              </div>
            </div>
          );
        })}
    </EhrTimeSlotContainer>
  );
};

const EhrTimeSlotContainer = styled.div`
  @media (max-width: 680px) {
    margin-top: 20px;
  }
`;

const DateTime = styled.div.attrs<{ $marginTopDisabled?: boolean }>(
  (props) => ({
    $marginTopDisabled: props.$marginTopDisabled ?? false,
  }),
)`
  font-size: 14px;
  margin: 0.5rem 0;
  margin-top: ${(props) => (props.$marginTopDisabled ? '0' : '16px')};
`;

const TimeSlotWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const TimeSlotButton = styled(Button)`
  width: 80px;

  &:enabled {
    background-color: var(--action-color) !important;
  }
`;

export default EhrTimeSlots;
